import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { groupUsersBySection, ACCOUNT_SECTIONS, resolveAccountStatus } from "@/lib/accountStatus";
import {
  getWorkspaceRoleBadgeClass,
  getWorkspaceRoleLabel,
} from "@/lib/userRoles";
import {
  canShowDeleteButton,
  canShowSuspendButton,
  getUserRoleHint,
  resolveUserDeleteBlockReason,
  isProtectedSystemUser,
} from "@/lib/userDeleteEligibility";

const SECTION_META = {
  [ACCOUNT_SECTIONS.OPERATIONAL]: {
    title: "Usuarios activos del equipo",
    description: "Auxiliares, consulta y operadores con acceso operativo válido.",
  },
  [ACCOUNT_SECTIONS.BUYER_ACCOUNT]: {
    title: "Cuentas titular / comprador",
    description:
      "Titulares con registro completado. Revisar trial o licencia; no mezclar con auxiliares del equipo.",
  },
  [ACCOUNT_SECTIONS.PENDING]: {
    title: "Pendientes e invitados",
    description: "Registro incompleto o invitación sin contraseña definida.",
  },
  [ACCOUNT_SECTIONS.PLATFORM]: {
    title: "Plataforma BNT / diseñador",
    description: "Cuentas técnicas de administración; sin flujo de solicitud de licencia comprador.",
  },
};

function AccountStatusBadge({ status }) {
  if (!status?.label) return null;
  const tone =
    status.section === ACCOUNT_SECTIONS.PENDING
      ? "bg-amber-100 text-amber-900"
      : status.section === ACCOUNT_SECTIONS.BUYER_ACCOUNT
        ? "bg-sky-100 text-sky-900"
        : status.section === ACCOUNT_SECTIONS.PLATFORM
          ? "bg-violet-100 text-violet-900"
          : "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm ${tone}`}>
      {status.label}
    </span>
  );
}

function UserRow({
  u,
  showResellerUi,
  currentUserId,
  onEdit,
  onDelete,
  onSuspend,
  suspendingId,
}) {
  const rolLbl =
    u.is_admin_owner && u.role !== "platform_designer"
      ? "Propietario"
      : getWorkspaceRoleLabel(u.role, u);
  const rolCls =
    u.is_admin_owner && u.role !== "platform_designer"
      ? "bg-emerald-600 text-white"
      : getWorkspaceRoleBadgeClass(u.role, u);
  const cliCount = u.clients_count ?? 0;
  let cliText;
  if (u.is_admin_owner || (u.role === "admin" && !u.is_buyer_tenant)) {
    cliText = "Todas";
  } else if (u.role === "buyer_admin") {
    cliText = `${cliCount} empresa(s)`;
  } else {
    cliText = `${cliCount} asignadas`;
  }
  if (u.role === "revendedor") cliText = "—";
  const rp = u.reseller_profile || {};
  const facturaTxt = u.role === "revendedor" ? (rp.requires_invoice ? "Sí" : "No") : "—";
  const status = resolveAccountStatus(u);
  const deleteBlock = resolveUserDeleteBlockReason(u, currentUserId);
  const showDelete = canShowDeleteButton(u, currentUserId);
  const showSuspend = canShowSuspendButton(u);
  const roleHint = getUserRoleHint(u);

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-3 py-2 font-mono text-[11px]">
        {u.role === "buyer_admin" && u.portal_username
          ? `${u.portal_username} (@buyer-tenant)`
          : u.email}
        {roleHint ? (
          <span className="block text-[9px] text-slate-500 font-sans mt-0.5">{roleHint}</span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-slate-700">{u.name || "—"}</td>
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${rolCls}`}>{rolLbl}</span>
      </td>
      <td className="px-3 py-2">
        <AccountStatusBadge status={status} />
      </td>
      {showResellerUi ? (
        <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">{facturaTxt}</td>
      ) : null}
      <td className="px-3 py-2 text-slate-600 text-[11px]">{cliText}</td>
      <td className="px-3 py-2 text-center">{u.has_password ? "✓" : "—"}</td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            onClick={() => onEdit(u)}
            className="h-7 text-[11px] rounded-sm bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
            title="Modificar datos del usuario"
          >
            <Pencil className="w-3 h-3 mr-1" /> Modificar datos
          </Button>
          {showSuspend ? (
            <Button
              size="sm"
              variant="outline"
              disabled={suspendingId === u.id}
              onClick={() => onSuspend?.(u)}
              className="h-7 text-[11px] rounded-sm border-slate-300"
              title="Suspender cuenta con historial"
            >
              Suspender
            </Button>
          ) : null}
          {showDelete ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(u)}
              className="h-7 text-[11px] rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50"
              title="Eliminar usuario"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          ) : deleteBlock && !isProtectedSystemUser(u) ? (
            <span className="text-[9px] text-slate-500 max-w-[140px] text-right leading-tight" title={deleteBlock}>
              No se puede borrar
            </span>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export default function UserManagementGroupedTables({
  users,
  showResellerUi,
  currentUserId,
  onEdit,
  onDelete,
  onSuspend,
  suspendingId,
  emptyMessage = "No hay usuarios registrados.",
}) {
  const groups = groupUsersBySection(users);
  const order = [
    ACCOUNT_SECTIONS.OPERATIONAL,
    ACCOUNT_SECTIONS.BUYER_ACCOUNT,
    ACCOUNT_SECTIONS.PENDING,
    ACCOUNT_SECTIONS.PLATFORM,
  ];

  if (!users.length) {
    return <p className="text-xs text-slate-500">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-6">
      {order.map((sectionKey) => {
        const rows = groups[sectionKey] || [];
        if (!rows.length) return null;
        const meta = SECTION_META[sectionKey] || { title: sectionKey, description: "" };
        return (
          <div key={sectionKey} data-testid={`user-section-${sectionKey}`}>
            <div className="mb-2">
              <h4 className="text-xs font-semibold text-slate-800 m-0">{meta.title}</h4>
              <p className="text-[11px] text-slate-500 m-0 mt-0.5">{meta.description}</p>
            </div>
            <div className="border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-xs min-w-[1100px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[230px]">
                      Correo
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[140px]">
                      Nombre
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[150px]">
                      Rol
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[160px]">
                      Estatus
                    </th>
                    {showResellerUi ? (
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[72px]">
                        Factura
                      </th>
                    ) : null}
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                      Empresas visibles
                    </th>
                    <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[90px]">
                      Clave
                    </th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[180px]">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      showResellerUi={showResellerUi}
                      currentUserId={currentUserId}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onSuspend={onSuspend}
                      suspendingId={suspendingId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
